import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ArrowRight,
  Sparkles,
  ClipboardList,
  AlertTriangle,
  Siren,
  Info,
  Workflow,
  Puzzle,
  CheckCircle2,
  XCircle,
  Plug,
  Loader2,
  Lock,
  Activity,
  BookOpen
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useSubscription } from '../hooks/useSubscription';
import { useAssistantWorkflowActions } from '../hooks/useAssistantWorkflowActions';
import { isAdminOrOps } from '../utils/roles';
import { hasRealIntegration } from '@/lib/integrationStatus';
import { WorkflowSuggestionsChat } from '@/components/WorkflowSuggestionsChat';
import { toast } from 'sonner';
import { SlackConnectResultModal, type SlackConnectResult } from '../components/SlackConnectResultModal';
import { Card, CardContent, CardDescription, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ClusterStatus } from '../components/ClusterStatus';
import { OnboardingChecklist } from '../components/OnboardingChecklist';
import { apiClient } from '../api/client';
import { getStatusLabel } from '../lib/incidents';
import { useTheme } from '@/components/theme-provider';
import { ShipWheel } from 'lucide-react';
import slackColorIcon from '../assets/icons/slack-color.png';
import posthogIcon from '../assets/icons/posthog-logo-stacked.svg';
import posthogLightIcon from '../assets/icons/posthog-light.png';
import vercelIcon from '../assets/icons/vercel.svg';
import vercelLightIcon from '../assets/icons/vercel-light.svg';
import railwayIcon from '../assets/icons/railway.svg';
import flyioIcon from '../assets/icons/flyio.svg';
import nebiusIcon from '../assets/icons/NBIS.svg';
import { ReactComponent as BeamIcon } from '../assets/icons/beam.svg';
import { ReactComponent as DaytonaIcon } from '../assets/icons/daytona.svg';
import supabaseIcon from '../assets/icons/supabase-logo-icon.svg';
import { ReactComponent as PlanetScaleIcon } from '../assets/icons/planetscale-mono.svg';
import neonIcon from '../assets/icons/neon-logomark-light-color.svg';
import clickhouseIcon from '../assets/icons/clickhouse.svg';
import jenkinsIcon from '../assets/icons/Jenkins.svg';
import { ReactComponent as CircleCIIcon } from '../assets/icons/circleci-mono.svg';
import { ReactComponent as SonarCloudIcon } from '../assets/icons/sonarcloud-mono.svg';
import { ReactComponent as OktaIcon } from '../assets/icons/okta-mono.svg';
import databricksIcon from '../assets/icons/databricks.png';
import { ReactComponent as GCPIcon } from '../assets/icons/gcp.svg';
import terraformIcon from '../assets/icons/HashiCorp Terraform.svg';
import pulumiIcon from '../assets/icons/pulumi-logo-mark-color.svg';
import vaultIcon from '../assets/icons/hashicorp-vault.svg';
import infisicalIcon from '../assets/icons/infisical.svg';
import jiraIcon from '../assets/icons/jira.svg';
import pagerdutyColorIcon from '../assets/icons/pagerduty-icon.svg';
import datadogLightIcon from '../assets/icons/datadog-light-mode.svg';
import datadogDarkIcon from '../assets/icons/datadog-dark-mode.png';
import awsLightLogo from '../assets/icons/aws.svg';
import awsDarkLogo from '../assets/icons/aws-dark.svg';
import argocdIcon from '../assets/icons/argocd.svg';
import fluxcdIcon from '../assets/icons/flux-cd.svg';
import karpenterIcon from '../assets/icons/karpenter.svg';
import kyvernoIcon from '../assets/icons/kyverno.svg';
import trivyIcon from '../assets/icons/trivy.svg';
import kestrelLightLogo from '../assets/icons/kestrel-light.svg';
import kestrelDarkLogo from '../assets/icons/kestrel-dark.svg';
import gitlabIcon from '../assets/icons/gitlab-clean.svg';
import confluenceIcon from '../assets/icons/confluence.svg';
import ociIcon from '../assets/icons/oci.png';
import cloudflareIcon from '../assets/icons/cloudflare.svg';
import linearIcon from '../assets/icons/linear.png';
import linearDarkIcon from '../assets/icons/linear-dark-mode.png';
import gleanIcon from '../assets/icons/glean.svg';
import notionIcon from '../assets/icons/notion-logo-no-background.png';
import notionLightIcon from '../assets/icons/notion-logo-white.png';

interface HomeStats {
  // Incident counts
  totalIncidents: number;
  analyzed: number;      // "Analysis Complete" - ready to apply
  remediated: number;    // Fix applied
  resolved: number;      // Manually closed
  // Workflow execution counts
  activeWorkflows: number;
  totalExecutions: number;
  completedExecutions: number;
  failedExecutions: number;
  runningExecutions: number;
  loading: boolean;
  error: string | null;
}



// Quick actions when the legacy products (Incidents, Cloud AI Copilot) are
// hidden: the workflow surfaces take their place.
const quickActions = [
  {
    title: 'Workflows',
    description: 'Create, manage, and monitor workflows triggered by signals from 40+ integrations',
    href: '/workflows',
    icon: Workflow,
    productKey: 'workflows' as const,
  },
  {
    title: 'Executions',
    description: 'Inspect workflow runs with step-level logs and timing to see what each block did',
    href: '/workflow-executions',
    icon: Activity,
    productKey: 'workflows' as const,
  },
  {
    title: 'Library',
    description: 'Browse workflow suggestions for your integrations and activate them in one click',
    href: '/workflows/suggestions',
    icon: BookOpen,
    productKey: 'workflows' as const,
  },
];

// Quick actions for tenants with the legacy products re-enabled: the original
// one-card-per-product layout.
const legacyQuickActions = [
  quickActions[0],
  {
    title: 'Incident Response',
    description: 'AI-powered Cloud incident detection, root cause analysis, and automated remediation',
    href: '/incidents',
    icon: Siren,
    productKey: 'incident_response' as const,
  },
  {
    title: 'Cloud AI Copilot',
    description: 'Chat with AI to get help with your cloud infrastructure and troubleshooting',
    href: '/cloud-ai-copilot',
    icon: Sparkles,
    productKey: 'copilot' as const,
  },
];

function IntegrationsStatusCard() {
  const [integrations, setIntegrations] = useState<{ id: string; name: string; connected: boolean; setup_path?: string }[]>([]);
  const [clusterStats, setClusterStats] = useState<{ total: number; connected: number; degraded: number; offline: number } | null>(null);
  const { theme } = useTheme();
  const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  useEffect(() => {
    apiClient.getWorkflowIntegrationsStatus().then(setIntegrations).catch(() => {});
    apiClient.getClusterStats().then(s => {
      setClusterStats({
        total: s.total_clusters,
        connected: s.connected_clusters,
        degraded: s.degraded_clusters || 0,
        offline: s.disconnected_clusters,
      });
    }).catch(() => {});
  }, []);

  const showOrder = ['kubernetes', 'aws', 'gcp', 'oci', 'cloudflare', 'github', 'gitlab', 'slack', 'jira', 'confluence', 'pagerduty', 'datadog', 'argocd', 'argo-rollouts', 'fluxcd', 'karpenter', 'kyverno', 'trivy', 'jenkins', 'circleci', 'sonarcloud', 'okta', 'databricks', 'terraform', 'pulumi', 'posthog', 'vercel', 'railway', 'flyio', 'nebius', 'beam', 'daytona', 'supabase', 'planetscale', 'neon', 'clickhouse', 'linear', 'glean', 'notion'];
  const seen = new Set<string>();
  const filtered = integrations
    .filter(i => {
      if (!showOrder.includes(i.id) || seen.has(i.id)) return false;
      seen.add(i.id);
      return true;
    })
    // Connected integrations first, then the rest; catalog order within each group.
    .sort((a, b) =>
      (Number(b.connected) - Number(a.connected)) ||
      (showOrder.indexOf(a.id) - showOrder.indexOf(b.id))
    );

  const getIcon = (id: string) => {
    const s = { width: 14, height: 14 };
    switch (id) {
      case 'github': return <img src={isDark ? 'https://github.githubassets.com/favicons/favicon-dark.svg' : 'https://github.githubassets.com/favicons/favicon.svg'} alt="" style={s} />;
      case 'gitlab': return <img src={gitlabIcon} alt="" style={s} />;
      case 'slack': return <img src={slackColorIcon} alt="" style={s} />;
      case 'posthog': return <img src={isDark ? posthogLightIcon : posthogIcon} alt="" style={s} className="object-contain" />;
      case 'vercel': return <img src={isDark ? vercelLightIcon : vercelIcon} alt="" style={{ width: 12, height: 12 }} className="object-contain" />;
      case 'railway': return <img src={railwayIcon} alt="" style={{ width: 12, height: 12, filter: isDark ? 'invert(1)' : 'none' }} className="object-contain" />;
      case 'flyio': return <img src={flyioIcon} alt="" style={{ width: 12, height: 12 }} className="object-contain" />;
      case 'nebius': return <img src={nebiusIcon} alt="" style={{ width: 12, height: 12 }} className="object-contain" />;
      case 'beam': return <BeamIcon style={{ width: 12, height: 12 }} className="text-foreground" />;
      case 'daytona': return <DaytonaIcon style={{ width: 12, height: 12 }} className="text-foreground" />;
      case 'supabase': return <img src={supabaseIcon} alt="Supabase" style={{ width: 12, height: 12 }} className="object-contain" />;
      case 'planetscale': return <PlanetScaleIcon style={{ width: 12, height: 12 }} className="text-foreground" />;
      case 'neon': return <img src={neonIcon} alt="Neon" style={{ width: 12, height: 12 }} className="object-contain" />;
      case 'clickhouse': return <img src={clickhouseIcon} alt="ClickHouse" style={{ width: 12, height: 12 }} className="object-contain" />;
      case 'jenkins': return <img src={jenkinsIcon} alt="Jenkins" style={{ width: 12, height: 12 }} className="object-contain" />;
      case 'circleci': return <CircleCIIcon style={{ width: 12, height: 12 }} className="text-foreground" />;
      case 'sonarcloud': return <SonarCloudIcon style={{ width: 12, height: 12 }} className="text-foreground" />;
      case 'okta': return <OktaIcon style={{ width: 12, height: 12 }} className="text-foreground" />;
      case 'databricks': return <img src={databricksIcon} alt="Databricks" style={{ width: 12, height: 12 }} className="object-contain" />;
      case 'terraform': return <img src={terraformIcon} alt="Terraform" style={{ width: 12, height: 12 }} className="object-contain" />;
      case 'pulumi': return <img src={pulumiIcon} alt="Pulumi" style={{ width: 12, height: 12 }} className="object-contain" />;
      case 'vault': return <img src={vaultIcon} alt="Vault" style={{ width: 12, height: 12 }} className="object-contain" />;
      case 'infisical': return <img src={infisicalIcon} alt="Infisical" style={{ width: 12, height: 12 }} className="object-contain rounded-sm" />;
      case 'jira': return <img src={jiraIcon} alt="" style={s} />;
      case 'confluence': return <img src={confluenceIcon} alt="" style={s} />;
      case 'pagerduty': return <img src={pagerdutyColorIcon} alt="" style={s} />;
      case 'datadog': return <img src={isDark ? datadogDarkIcon : datadogLightIcon} alt="" style={s} />;
      case 'aws': case 'aws-cost': case 'aws-ops': case 'aws-security': case 'aws-data': return <img src={isDark ? awsDarkLogo : awsLightLogo} alt="" style={s} />;
      case 'gcp': case 'gcp-cost': case 'gcp-security': case 'gcp-data': return <GCPIcon style={s} />;
      case 'oci': return <img src={ociIcon} alt="" style={s} />;
      case 'cloudflare': return <img src={cloudflareIcon} alt="" style={s} className="object-contain" />;
      case 'argocd': return <img src={argocdIcon} alt="" style={s} />;
      case 'argo-rollouts': return <img src={argocdIcon} alt="" style={s} />;
      case 'fluxcd': return <img src={fluxcdIcon} alt="" style={s} />;
      case 'karpenter': return <img src={karpenterIcon} alt="" style={s} />;
      case 'kyverno': return <img src={kyvernoIcon} alt="" style={s} />;
      case 'trivy': return <img src={trivyIcon} alt="" style={s} />;
      case 'kubernetes': return <ShipWheel style={s} className="text-blue-500" />;
      case 'linear': return <img src={isDark ? linearDarkIcon : linearIcon} alt="" style={s} />;
      case 'glean': return <img src={gleanIcon} alt="" style={s} />;
      case 'notion': return <img src={isDark ? notionLightIcon : notionIcon} alt="" style={s} className="object-contain" />;
      default: return null;
    }
  };

  return (
    <div className="space-y-3">
      <h3 className="text-lg font-medium text-foreground">Integrations</h3>
      <div className="space-y-1 max-h-[132px] overflow-y-auto" style={{ scrollbarWidth: 'none' }}>
        {filtered.length === 0 ? (
          <p className="text-xs text-muted-foreground py-2">No integrations connected yet. Connect your tools in Integrations.</p>
        ) : filtered.map(i => {
          const isK8s = i.id === 'kubernetes';
          return (
            <div key={i.id} className="flex items-center gap-2 py-0.5">
              <div className="shrink-0 w-4 flex items-center justify-center">{getIcon(i.id)}</div>
              <span className="text-sm text-muted-foreground">{i.name}</span>
              <div className="flex items-center gap-1.5 ml-auto shrink-0">
                {isK8s && clusterStats && clusterStats.total > 0 ? (
                  <div className="flex items-center gap-1.5 text-[11px]">
                    <span className="text-green-500">{clusterStats.connected} connected</span>
                    {clusterStats.degraded > 0 && <span className="text-amber-500">{clusterStats.degraded} degraded</span>}
                    {clusterStats.offline > 0 && <span className="text-red-500">{clusterStats.offline} offline</span>}
                  </div>
                ) : (
                  i.connected
                    ? <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                    : <XCircle className="w-3.5 h-3.5 text-muted-foreground/40" />
                )}
              </div>
            </div>
          );
        })}
      </div>
      {filtered.length > 5 && (
        <div className="flex justify-center pt-0.5">
          <svg className="w-3.5 h-3.5 text-muted-foreground/50 animate-bounce" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
        </div>
      )}
    </div>
  );
}
// Helper function to parse time strings like "14 minutes ago" back to timestamps for sorting
const parseTimeAgo = (timeString: string): number => {
  const now = Date.now();
  
  if (timeString === 'Just now') {
    return now;
  }
  
  const match = timeString.match(/(\d+)\s*(minute|hour|day)s?\s*ago/);
  if (!match) {
    return now; // Default to now if can't parse
  }
  
  const value = parseInt(match[1]);
  const unit = match[2];
  
  switch (unit) {
    case 'minute':
      return now - (value * 60 * 1000);
    case 'hour':
      return now - (value * 60 * 60 * 1000);
    case 'day':
      return now - (value * 24 * 60 * 60 * 1000);
    default:
      return now;
  }
};

// Helper function to format timestamps to relative time
function formatTimeAgo(timestamp: number): string {
  const now = Date.now();
  const timestampMs = timestamp * 1000; // Convert Unix timestamp to milliseconds
  const diff = now - timestampMs;
  
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  
  if (days > 0) {
    return `${days} day${days > 1 ? 's' : ''} ago`;
  } else if (hours > 0) {
    return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  } else if (minutes > 0) {
    return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  } else {
    return 'Just now';
  }
}

export function Home() {
  const { isLoading, user } = useAuth();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { access, isProductBilling, loading: subscriptionLoading } = useSubscription();
  // Legacy products (Incidents, Cloud AI Copilot) are hidden unless the tenant
  // has been re-enabled via tenants.legacy_products_enabled.
  const legacyProductsEnabled = user?.legacy_products_enabled === true;
  // Only show the Incidents card for tenants with an active Incident Response
  // trial or subscription. While loading, keep it hidden to avoid a flash.
  const showIncidents = legacyProductsEnabled && !subscriptionLoading && access.incident_response;
  const isDarkMode = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  const isOrgAdmin = user?.role === 'org_admin';
  // The Build tab creates workflows, so it's shown only to roles that can
  // access the workflow builder; other roles see the classic overview.
  const canBuild = isAdminOrOps(user?.role);
  const [activeTab, setActiveTabState] = useState<'build' | 'overview'>(() => {
    try {
      return localStorage.getItem('kestrel-home-tab') === 'overview' ? 'overview' : 'build';
    } catch {
      return 'build';
    }
  });
  const switchTab = (tab: 'build' | 'overview') => {
    setActiveTabState(tab);
    try { localStorage.setItem('kestrel-home-tab', tab); } catch { /* ignore */ }
  };
  // Simulated chat: with no real integration connected, the Build tab's agent
  // treats every integration as connected and creates sandboxed (simulated)
  // workflows — mirroring the builder's simulated mode — so brand-new users
  // can explore what they could automate before onboarding anything.
  const [chatSimulated, setChatSimulated] = useState(false);
  useEffect(() => {
    if (!canBuild || isLoading) return;
    apiClient
      .getWorkflowIntegrationsStatus()
      .then(statuses => setChatSimulated(!hasRealIntegration(statuses)))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canBuild, isLoading]);
  const { saveSuggestion, createSuggestion, renameDialogElement } = useAssistantWorkflowActions({ simulated: chatSimulated });
  const [showChecklist, setShowChecklist] = useState(false);
  const [stats, setStats] = useState<HomeStats>({
    totalIncidents: 0,
    analyzed: 0,
    remediated: 0,
    resolved: 0,
    activeWorkflows: 0,
    totalExecutions: 0,
    completedExecutions: 0,
    failedExecutions: 0,
    runningExecutions: 0,
    loading: true,
    error: null,
  });
  
  const [recentActivity, setRecentActivity] = useState<{
    activities: any[];
    loading: boolean;
    error: string | null;
  }>({
    activities: [],
    loading: true,
    error: null,
  });

  const [slackResult, setSlackResult] = useState<SlackConnectResult | null>(null);

  // Target for portaling the Build/Overview pill into the PageHeader strip,
  // so it sits at the same vertical level as the mode toggle.
  const [headerSlot, setHeaderSlot] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setHeaderSlot(document.getElementById('page-header-center-slot'));
  }, []);

  // New org_admins who haven't finished (or skipped) the setup wizard land
  // there instead of the full dashboard. Rendering is gated on this check so
  // the dashboard doesn't flash before the redirect.
  const [setupChecked, setSetupChecked] = useState(false);
  useEffect(() => {
    if (isLoading) return;
    if (!isOrgAdmin) {
      setSetupChecked(true);
      return;
    }
    let cancelled = false;
    apiClient
      .getSetupWizardState()
      .then((state) => {
        if (cancelled) return;
        if (!state.completed) {
          navigate('/setup', { replace: true });
        } else {
          setSetupChecked(true);
        }
      })
      .catch(() => {
        // On error, stay on the dashboard.
        if (!cancelled) setSetupChecked(true);
      });
    return () => {
      cancelled = true;
    };
  }, [isLoading, isOrgAdmin, navigate]);

  // Auto-trigger founder Slack Connect channel creation when arriving from the
  // welcome email's "Chat with us on Slack" link (?slack_connect=1).
  useEffect(() => {
    if (isLoading) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('slack_connect') !== '1') return;
    // Strip the param so a refresh doesn't re-trigger.
    params.delete('slack_connect');
    const newQuery = params.toString();
    navigate({ pathname: '/home', search: newQuery ? `?${newQuery}` : '' }, { replace: true });
    (async () => {
      try {
        const res = await apiClient.createFounderSlackChannel();
        setSlackResult(res);
      } catch (error: any) {
        const status = error?.response?.status;
        if (status === 503) {
          toast.error('Slack Connect isn\'t available right now. Please try again later.', { duration: 8000 });
        } else {
          toast.error('Couldn\'t set up your Slack channel. Please try again or email support@usekestrel.ai.', { duration: 8000 });
        }
        console.error('Failed to create founder Slack channel:', error);
      }
    })();
  }, [isLoading, navigate]);

  useEffect(() => {
    if (isLoading) return;
    const twentyFourHoursAgo = Math.floor(Date.now() / 1000) - (24 * 60 * 60);
    const now = Math.floor(Date.now() / 1000);

    // Fetch workflow stats independently (fast)
    const dateFrom24h = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
    Promise.all([
      apiClient.listAllExecutions({ page_size: 100, date_from: dateFrom24h }).catch(() => ({ executions: [], total: 0 })),
      apiClient.getWorkflowStats().catch(() => ({ total_workflows: 0, active_workflows: 0, total_executions: 0, status_breakdown: {}, daily_executions: [] })),
    ]).then(([executionsResp, wfStats]: any[]) => {
      const executions = executionsResp.executions || [];
      // Use daily_executions from stats for accurate 24h breakdown (not capped by page_size)
      const today = new Date().toISOString().slice(0, 10);
      const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
      const dailyExecs = (wfStats.daily_executions || []) as { date: string; completed: number; failed: number; running: number; waiting: number }[];
      const last24h = dailyExecs.filter((d: any) => d.date === today || d.date === yesterday);
      const completed24h = last24h.reduce((sum: number, d: any) => sum + (d.completed || 0), 0);
      const failed24h = last24h.reduce((sum: number, d: any) => sum + (d.failed || 0), 0);
      const running24h = last24h.reduce((sum: number, d: any) => sum + (d.running || 0) + (d.waiting || 0), 0);
      setStats(prev => ({
        ...prev,
        activeWorkflows: wfStats.active_workflows || 0,
        totalExecutions: completed24h + failed24h + running24h,
        completedExecutions: completed24h,
        failedExecutions: failed24h,
        runningExecutions: running24h,
        loading: false,
      }));

      // Also add executions to recent activity immediately
      const execActivities = executions.map((exec: any) => ({
        type: 'workflow_execution' as const,
        action: `Workflow ${exec.status}: ${exec.workflow_name || 'Unknown'}`,
        details: exec.error_message ? exec.error_message.substring(0, 100) : `Duration: ${exec.completed_at ? Math.round((new Date(exec.completed_at).getTime() - new Date(exec.started_at).getTime()) / 1000) + 's' : 'running'}`,
        time: formatTimeAgo(Math.floor(new Date(exec.started_at).getTime() / 1000)),
        timestamp: new Date(exec.started_at).getTime(),
        data: exec,
      }));
      setRecentActivity(prev => {
        const merged = [...prev.activities.filter((a: any) => a.type !== 'workflow_execution'), ...execActivities]
          .sort((a, b) => b.timestamp - a.timestamp).slice(0, 6);
        return { ...prev, activities: merged, loading: false };
      });
    });

    // Fetch incident stats independently (slow — may timeout)
    apiClient.getIncidentStats(twentyFourHoursAgo, now)
      .then((incidentStats: any) => {
        setStats(prev => ({
          ...prev,
          totalIncidents: incidentStats.total_incidents || 0,
          analyzed: incidentStats.analyzed_count || 0,
          remediated: incidentStats.remediated_count || 0,
          resolved: incidentStats.resolved_count || 0,
        }));
      })
      .catch(() => {});

    // Fetch recent activity (incidents, policies, etc.) independently
    Promise.all([
      apiClient.getRecentPolicies(undefined, 20).catch(() => ({ activities: [] })),
      apiClient.getRecentAuthorizationPolicies(undefined, 20).catch(() => ({ activities: [] })),
      apiClient.getRecentClusters(20).catch(() => ({ activities: [] })),
      apiClient.getRecentUsers(20).catch(() => ({ activities: [] })),
      apiClient.getRecentIncidents(20).catch(() => ({ incidents: [] })),
    ]).then(([recentPolicies, recentAuthPolicies, recentClusters, recentUsers, recentIncidentsResp]: any[]) => {
      const incidentActivities = (recentIncidentsResp.incidents || []).map((incident: any) => ({
        type: 'incident' as const,
        action: `Incident detected: ${incident.type}`,
        details: `${incident.primary_resource} in ${incident.cluster_name} - ${getStatusLabel(incident.status)}`,
        time: formatTimeAgo(incident.detected_at),
        timestamp: incident.detected_at * 1000,
        data: incident,
      }));
      const otherActivities = [
        ...recentPolicies.activities.map((a: any) => ({ ...a, timestamp: parseTimeAgo(a.time) })),
        ...recentAuthPolicies.activities.map((a: any) => ({ ...a, timestamp: parseTimeAgo(a.time) })),
        ...recentClusters.activities.map((a: any) => ({ ...a, timestamp: parseTimeAgo(a.time) })),
        ...recentUsers.activities.map((a: any) => ({ ...a, timestamp: parseTimeAgo(a.time) })),
        ...incidentActivities,
      ];
      setRecentActivity(prev => {
        const merged = [...otherActivities, ...prev.activities.filter((a: any) => a.type === 'workflow_execution')]
          .sort((a, b) => b.timestamp - a.timestamp).slice(0, 6);
        return { activities: merged, loading: false, error: null };
      });
    }).catch(() => {
      setRecentActivity(prev => ({ ...prev, loading: false }));
    });
  }, [isLoading]);

  if (isLoading || !setupChecked) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }




  return (
    // -mt-4 cancels the layout's top padding so content sits tight under the
    // Build/Overview pill in the header strip.
    <div className="-mt-4 space-y-6">
      <SlackConnectResultModal
        open={!!slackResult}
        onClose={() => setSlackResult(null)}
        result={slackResult}
        email={user?.email}
      />

      {/* Build / Overview toggle — centered segmented control (ChatGPT-style),
          portaled into the PageHeader strip so it sits at the same vertical
          level as the mode toggle. Falls back to inline if the slot is gone. */}
      {canBuild && (() => {
        const pill = (
          <div className="inline-flex items-center rounded-full border border-border bg-muted p-1">
            <button
              onClick={() => switchTab('build')}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                activeTab === 'build'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Build
            </button>
            <button
              onClick={() => switchTab('overview')}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                activeTab === 'overview'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Overview
            </button>
          </div>
        );
        return headerSlot ? createPortal(pill, headerSlot) : <div className="flex justify-center">{pill}</div>;
      })()}

      {/* Build tab — Workflow Assistant chat, full-page like ChatGPT: a
          centered column with no card chrome. Kept mounted (CSS-hidden) so
          the conversation survives tab switches. */}
      {canBuild && (
        <div className={activeTab === 'build' ? 'block' : 'hidden'}>
          <div className="max-w-4xl mx-auto h-[calc(100vh-84px)] min-h-[480px]">
            <WorkflowSuggestionsChat
              hideHeader
              startBlank
              simulated={chatSimulated}
              onSuggestionSave={saveSuggestion}
              onSuggestionCreate={createSuggestion}
            />
          </div>
        </div>
      )}

      <div className={!canBuild || activeTab === 'overview' ? 'space-y-8' : 'hidden'}>
      {/* Page heading — lives inside the Overview tab because the chat view
          doesn't need it (the global PageHeader no longer titles /home). */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">Home</h1>
        <p className="text-muted-foreground mt-1 text-sm leading-relaxed">
          An overview of your Platform&apos;s status and recent activity.
        </p>
      </div>
      {/* Top section: stats + quick actions alongside checklist sidebar */}
      <div className={isOrgAdmin && showChecklist ? 'flex gap-6 items-start' : ''}>
        {/* Main content — stats, error, quick actions */}
        <div className="flex-1 min-w-0 space-y-8">
          {/* Stats Grid */}
          <div className={`grid grid-cols-1 gap-5 ${showIncidents ? 'lg:grid-cols-3' : 'lg:grid-cols-2'}`}>
            {/* Workflows Status */}
            <Card className="lg:col-span-1">
              <CardContent className="p-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-medium text-foreground">Workflows</h3>
                    <TooltipProvider delayDuration={200}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="ml-auto cursor-help">
                            <Info className="h-4 w-4 text-muted-foreground" />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Last 24 hrs</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <div className="w-2.5 h-2.5 rounded-full bg-green-500"></div>
                        <span className="text-sm text-muted-foreground">Completed</span>
                      </div>
                      <span className="text-lg font-bold text-foreground">
                        {stats.loading ? '...' : stats.completedExecutions ?? 0}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <div className="w-2.5 h-2.5 rounded-full bg-red-500"></div>
                        <span className="text-sm text-muted-foreground">Failed</span>
                      </div>
                      <span className="text-lg font-bold text-foreground">
                        {stats.loading ? '...' : stats.failedExecutions ?? 0}
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <div className="w-2.5 h-2.5 rounded-full bg-blue-500"></div>
                        <span className="text-sm text-muted-foreground">Running</span>
                      </div>
                      <span className="text-lg font-bold text-foreground">
                        {stats.loading ? '...' : stats.runningExecutions ?? 0}
                      </span>
                    </div>
                  </div>
                  
                  <div className="pt-1.5 border-t border-border">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Total Executions</span>
                      <span className="text-lg font-bold text-foreground">
                        {stats.loading ? '...' : stats.totalExecutions ?? 0}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Incidents Status — only for tenants with Incident Response access */}
            {showIncidents && (
            <Card className="lg:col-span-1">
              <CardContent className="p-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-medium text-foreground">Incidents</h3>
                    <TooltipProvider delayDuration={200}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="ml-auto cursor-help">
                            <Info className="h-4 w-4 text-muted-foreground" />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Last 24 hrs</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <div className="w-2.5 h-2.5 rounded-full bg-orange-500"></div>
                        <span className="text-sm text-muted-foreground">Analyzed</span>
                      </div>
                      <span className="text-lg font-bold text-foreground">
                        {stats.loading ? '...' : stats.analyzed}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <div className="w-2.5 h-2.5 rounded-full bg-amber-500"></div>
                        <span className="text-sm text-muted-foreground">Fix Applied</span>
                      </div>
                      <span className="text-lg font-bold text-foreground">
                        {stats.loading ? '...' : stats.remediated}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <div className="w-2.5 h-2.5 rounded-full bg-green-500"></div>
                        <span className="text-sm text-muted-foreground">Resolved</span>
                      </div>
                      <span className="text-lg font-bold text-foreground">
                        {stats.loading ? '...' : stats.resolved}
                      </span>
                    </div>
                  </div>
                  
                  <div className="pt-1.5 border-t border-border">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Total Incidents</span>
                      <span className="text-lg font-bold text-foreground">
                        {stats.loading ? '...' : stats.totalIncidents}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            )}

            {/* Integrations Status */}
            <Card className="lg:col-span-1">
              <CardContent className="p-4">
                <IntegrationsStatusCard />
              </CardContent>
            </Card>
          </div>

          {/* Error Display */}
          {stats.error && (
            <Card className="border-destructive/20 bg-destructive/5">
              <CardContent className="p-4">
                <div className="text-destructive text-sm">
                  <strong>Connection Error:</strong> {stats.error}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Quick Actions */}
          <div>
            <h2 className="text-lg font-medium text-foreground mb-4">Quick Actions</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {(legacyProductsEnabled ? legacyQuickActions : quickActions)
                .map((action) => {
                // Mirror the sidebar: locked products route to billing with
                // product context instead of the (inaccessible) feature page.
                const isLocked =
                  isProductBilling && !subscriptionLoading && !access[action.productKey];
                return (
                  <Link
                    key={action.title}
                    to={isLocked ? `/billing?product=${action.productKey}` : action.href}
                    className="group"
                  >
                    <Card className="hover:shadow-md transition-shadow duration-200">
                      <CardContent className="p-4 sm:p-6">
                        <div>
                          <div className="bg-muted text-foreground rounded-lg inline-flex p-2 sm:p-3">
                            <action.icon className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" />
                          </div>
                        </div>
                        <div className="mt-3 sm:mt-4">
                          <CardTitle className="text-base sm:text-lg text-foreground group-hover:text-primary flex items-center gap-2">
                            {action.title}
                            {isLocked && <Lock className="h-4 w-4 text-muted-foreground/50" />}
                          </CardTitle>
                          <CardDescription className="mt-1 sm:mt-2 text-sm">
                            {action.description}
                          </CardDescription>
                        </div>
                        <div className="mt-3 sm:mt-4 flex items-center text-sm font-medium text-primary group-hover:text-primary/80">
                          <span>{isLocked ? 'Unlock' : 'Get started'}</span>
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>

        {/* Onboarding checklist — right sidebar, sticky. Only shown to org_admins. */}
        {isOrgAdmin && (
          <div className={showChecklist ? 'hidden xl:block w-96 flex-shrink-0 sticky top-0 self-stretch' : 'hidden'}>
            <OnboardingChecklist onVisibilityChange={setShowChecklist} />
          </div>
        )}
      </div>

      {/* Recent Activity — always full width, below the sidebar region */}
      <div>
        <h2 className="text-lg font-medium text-foreground mb-4">Recent Activity</h2>
        <Card>
          <CardContent className="p-0">
            <div className="divide-y divide-border">
              {recentActivity.loading ? (
                <div className="px-6 py-4 text-center text-muted-foreground">
                  Loading recent activity...
                </div>
              ) : recentActivity.error ? (
                <div className="px-6 py-4 text-center">
                  <div className="text-destructive text-sm font-medium mb-1">
                    {recentActivity.error}
                  </div>
                  <div className="text-muted-foreground text-xs">
                    Please check your connection and refresh to try again.
                  </div>
                </div>
              ) : recentActivity.activities.length > 0 ? (
                recentActivity.activities.map((item, index) => {
                  const isDark = isDarkMode;
                  const handleClick = () => {
                    if (item.type === 'workflow_execution' && item.data?.workflow_id && item.data?.id) {
                      navigate(`/workflows/${item.data.workflow_id}/executions/${item.data.id}`);
                    } else if (item.type === 'incident' && item.data?.id) {
                      navigate(`/incidents/${item.data.id}`);
                    }
                  };
                  const isClickable = (item.type === 'workflow_execution' && item.data?.workflow_id && item.data?.id)
                    || (item.type === 'incident' && item.data?.id);
                  return (
                  <div
                    key={index}
                    onClick={isClickable ? handleClick : undefined}
                    className={`px-4 sm:px-6 py-4 hover:bg-muted/50 transition-colors ${isClickable ? 'cursor-pointer' : ''}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-foreground">{item.action}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <p className="text-sm text-muted-foreground">{item.details}</p>
                          {item.type === 'workflow_execution' && item.data?.workflow_integrations && (
                            <div className="flex items-center gap-1">
                              {(item.data.workflow_integrations as string[]).map((integ: string) => {
                                const s = { width: 14, height: 14 };
                                const cls = 'rounded';
                                switch (integ) {
                                  case 'github': return <img key={integ} src={isDark ? 'https://github.githubassets.com/favicons/favicon-dark.svg' : 'https://github.githubassets.com/favicons/favicon.svg'} alt="GitHub" style={s} className={cls} />;
                                  case 'gitlab': return <img key={integ} src={gitlabIcon} alt="GitLab" style={s} className={cls} />;
                                  case 'slack': return <img key={integ} src={slackColorIcon} alt="Slack" style={s} className={cls} />;
                                  case 'posthog': return <img key={integ} src={isDark ? posthogLightIcon : posthogIcon} alt="PostHog" style={s} className={`${cls} object-contain`} />;
                                  case 'vercel': return <img key={integ} src={isDark ? vercelLightIcon : vercelIcon} alt="Vercel" style={{ width: 11, height: 11 }} className={`${cls} object-contain`} />;
                                  case 'railway': return <img key={integ} src={railwayIcon} alt="Railway" style={{ width: 11, height: 11, filter: isDark ? 'invert(1)' : 'none' }} className={`${cls} object-contain`} />;
                                  case 'flyio': return <img key={integ} src={flyioIcon} alt="Fly.io" style={{ width: 11, height: 11 }} className={`${cls} object-contain`} />;
                                  case 'nebius': return <img key={integ} src={nebiusIcon} alt="Nebius" style={{ width: 11, height: 11 }} className={`${cls} object-contain`} />;
                                  case 'beam': return <BeamIcon key={integ} style={{ width: 11, height: 11 }} className={`${cls} text-foreground`} />;
                                  case 'daytona': return <DaytonaIcon key={integ} style={{ width: 11, height: 11 }} className={`${cls} text-foreground`} />;
                                  case 'supabase': return <img key={integ} src={supabaseIcon} alt="Supabase" style={{ width: 11, height: 11 }} className={`${cls} object-contain`} />;
                                  case 'planetscale': return <PlanetScaleIcon key={integ} style={{ width: 11, height: 11 }} className={`${cls} text-foreground`} />;
                                  case 'neon': return <img key={integ} src={neonIcon} alt="Neon" style={{ width: 11, height: 11 }} className={`${cls} object-contain`} />;
                                  case 'clickhouse': return <img key={integ} src={clickhouseIcon} alt="ClickHouse" style={{ width: 11, height: 11 }} className={`${cls} object-contain`} />;
                                  case 'jenkins': return <img key={integ} src={jenkinsIcon} alt="Jenkins" style={{ width: 11, height: 11 }} className={`${cls} object-contain`} />;
                                  case 'circleci': return <CircleCIIcon key={integ} style={{ width: 11, height: 11 }} className={`${cls} text-foreground`} />;
                                  case 'sonarcloud': return <SonarCloudIcon key={integ} style={{ width: 11, height: 11 }} className={`${cls} text-foreground`} />;
                                  case 'okta': return <OktaIcon key={integ} style={{ width: 11, height: 11 }} className={`${cls} text-foreground`} />;
                                  case 'databricks': return <img key={integ} src={databricksIcon} alt="Databricks" style={{ width: 11, height: 11 }} className={`${cls} object-contain`} />;
                                  case 'terraform': return <img key={integ} src={terraformIcon} alt="Terraform" style={{ width: 11, height: 11 }} className={`${cls} object-contain`} />;
                                  case 'pulumi': return <img key={integ} src={pulumiIcon} alt="Pulumi" style={{ width: 11, height: 11 }} className={`${cls} object-contain`} />;
                                  case 'vault': return <img key={integ} src={vaultIcon} alt="Vault" style={{ width: 11, height: 11 }} className={`${cls} object-contain`} />;
                                  case 'infisical': return <img key={integ} src={infisicalIcon} alt="Infisical" style={{ width: 11, height: 11 }} className={`${cls} object-contain rounded-sm`} />;
                                  case 'jira': return <img key={integ} src={jiraIcon} alt="Jira" style={s} className={cls} />;
                                  case 'linear': return <img key={integ} src={isDark ? linearDarkIcon : linearIcon} alt="Linear" style={s} className={cls} />;
                                  case 'pagerduty': return <img key={integ} src={pagerdutyColorIcon} alt="PagerDuty" style={s} className={cls} />;
                                  case 'datadog': return <img key={integ} src={isDark ? datadogDarkIcon : datadogLightIcon} alt="Datadog" style={s} className={cls} />;
                                  case 'aws': case 'aws-cost': case 'aws-ops': case 'aws-security': case 'aws-data': return <img key={integ} src={isDark ? awsDarkLogo : awsLightLogo} alt="AWS" style={s} className={cls} />;
                                  case 'gcp': case 'gcp-cost': case 'gcp-security': case 'gcp-data': return <GCPIcon key={integ} style={s} className={cls} />;
                                  case 'argocd': return <img key={integ} src={argocdIcon} alt="ArgoCD" style={s} className={cls} />;
                                  case 'argo-rollouts': return <img key={integ} src={argocdIcon} alt="Argo Rollouts" style={s} className={cls} />;
                                  case 'fluxcd': return <img key={integ} src={fluxcdIcon} alt="Flux CD" style={s} className={cls} />;
                                  case 'karpenter': return <img key={integ} src={karpenterIcon} alt="Karpenter" style={s} className={cls} />;
                                  case 'kyverno': return <img key={integ} src={kyvernoIcon} alt="Kyverno" style={s} className={cls} />;
                                  case 'trivy': return <img key={integ} src={trivyIcon} alt="Trivy" style={s} className={cls} />;
                                  case 'kestrel': return <img key={integ} src={isDark ? kestrelDarkLogo : kestrelLightLogo} alt="Kestrel" style={s} className={cls} />;
                                  case 'kubernetes': return <ShipWheel key={integ} style={s} className="text-blue-500" />;
                                  case 'custom': return <Puzzle key={integ} style={{ width: 12, height: 12 }} className="text-foreground" />;
                                  default: return null;
                                }
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                      <Badge variant="secondary" className="flex-shrink-0 whitespace-nowrap">{item.time}</Badge>
                    </div>
                  </div>
                  );
                })
              ) : (
                <div className="px-6 py-4 text-center text-muted-foreground">
                  No recent activity
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
      </div>

      {renameDialogElement}
    </div>
    );
} 